<template>
  <v-card 
    class="mx-auto"
  >
    <v-toolbar flat>
      <v-toolbar-title>
        <v-icon size="32px">storage</v-icon>
        Provisioned Devices
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>
    
    <v-divider/>
    
    <v-card-text>
      <v-alert dense outlined type="error" v-if="error">
        {{ errorText }}
      </v-alert>
      
      <v-container>
        <v-row class="mx-2 my-1" align="end">
          <v-col cols="8">
            <v-text-field
              class="mr-5"
              v-model="search"
              append-icon="mdi-magnify"
              label="Search by device serial"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-select
              class="mr-5"
              v-model="productNames"
              :items="productNameList"
              item-text="label"
              item-value="value"
              label="Filter by product"
              clearable
              multiple
              hide-details
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="items"
              item-key="device_guid"
              :loading="loading"
              loadingText="Loading devices..."
              no-data-text="No devices"
              :page.sync="page"
              :items-per-page="itemsPerPage"
              sort-by="device_name"
              must-sort
              hide-default-footer
            >
              <template v-slot:item.device_name="{ item }">
                <device-label :device="item" />
              </template>
              <template v-slot:item.streaming_status="{ item }">
                <encoder-streaming-status-label v-if="item.device_type == 'encoder'" :device="item" />
                <decoder-streaming-status-label v-else-if="item.device_type == 'decoder'" :device="item" />
              </template>
              <template v-slot:item.device_ip="{ item }">
                <v-tooltip 
                  open-on-hover
                  open-delay="500"
                  right
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ item.device_ip }}
                    </span>
                  </template>
                  <span v-if="item.state.online">External IP: {{ item.external_ip }}</span>
                  <span v-else>Device Offline</span>
                </v-tooltip>
              </template>
              <template v-slot:item.firmware_version="{ item }">
                <firmware-version-label :device="item" />
              </template>
              <template v-slot:item.metadata="{ header, item }">
                <span v-if="header.key && item.hasMetadataKey(header.key)">{{  $helpers.getLabel(item.metadataValue(header.key)) }}</span>
                <span v-else-if="!header.key">
                  <v-chip
                    small
                    class="mr-1"
                    label
                    v-for="(data, index) in item.metadata"
                    v-bind:key="index"
                    outlined
                  >
                    <device-note v-if="data.key == 'note'" :device="item" />
                    <span v-else>
                      <strong>{{ $helpers.getLabel(data.key) }}</strong>: {{ $helpers.getLabel(data.value) }}
                    </span>
                  </v-chip>
                </span>
              </template>
              <template v-slot:item.state="{ header, item }">
                <span v-if="header.key && item.state && item.state[header.key]">{{  $helpers.getLabel(item.state[header.key]) }}</span>
              </template>
              <template v-slot:item.org_guid="{ item }">
                <span v-if="item.org_guid" class="subtitle-2">{{ $organizations.getOrg(item.org_guid).displayName() }}</span>
                <span v-else class="text--disabled">Not adopted</span>
              </template>
            </v-data-table>
            <data-table-pagination v-model="page" :paginationTokens="paginationTokens" />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
  import { mapGetters } from 'vuex'
  
  export default {
    name: 'AdminDeviceListing',
    data() {
      return {
        error: false,
        errorText: '',
        
        selectedDevice: false,
        
        search: '',
        productNames: [],
        awaitingSearch: false,

        productNameList: [
          { label: 'Edgecaster', value: 'edgecaster' },
          { label: 'Edgecaster Max', value: 'edgecaster_max' },
        ],
        
        loading: true,
        
        items: [],
        
        itemsPerPage: 50,
        page: 1,
        pageCount: 0,
        
        paginationTokens: []
      }
    },
    mounted() {
      this.searchDevices(0)
    },
    watch: {
      page() {
        this.searchDevices(this.page - 1)
      },
      itemsPerPage() {
        this.items = []
        this.page = 1
        this.searchDevices(this.page - 1)
      },
      search() {
        if (!this.awaitingSearch) {
          setTimeout(() => {
            this.items = []
            this.page = 1
            this.searchDevices(0)
            this.awaitingSearch = false
          }, 2000)
        }
        this.awaitingSearch = true
      },
      productNames() {
        if (!this.awaitingSearch) {
          setTimeout(() => {
            this.items = []
            this.page = 1
            this.searchDevices(0)
            this.awaitingSearch = false
          }, 500) // Reduce time because it's a dropdown
        }
        this.awaitingSearch = true
      }
    },
    computed: {
      ...mapGetters('userPreferences', ['deviceListLayout']),
      
      headers() {
        var sortableItems = [
          'streaming_status',
          'device_ip',
          'external_ip',
          'firmware_version',
          'serial_number',
          'mac_address'
        ]
        
        var headers = []
        
        if (!this.deviceListLayout.find(layout => layout.component == 'serial_number')) {
          headers.push({ text: 'Serial Number', align: 'center text-no-wrap', value: 'serial_number', sortable: true },)
        }
        
        headers.push({ text: 'Organization', align: 'left text-no-wrap', value: 'org_guid', sortable: true })
        headers.push({ text: 'Device', align: 'start', value: 'device_name', sortable: true })
        
        for (var index in this.deviceListLayout) {
          var layout = this.deviceListLayout[index]
          
          var thisHeader = {
            align: layout.class,
            value: layout.component,
            sortable: sortableItems.includes(layout.component) ? true : false
          }
          
          if (layout.component == 'metadata' && layout.key) {
            thisHeader.text = this.$helpers.getLabel(layout.key)
          } else {
            thisHeader.text = this.$helpers.getLabel(layout.component)
          }
          
          if (layout.key) {
            thisHeader.key = layout.key
          }
          
          headers.push(thisHeader)
        }
        
        return headers
      }
    },
    methods: {
      searchDevices(paginationIndex) {
        this.loading = true
        
        const paginationToken = this.paginationTokens[paginationIndex - 1]
        
        var queryParams = {
          'pagination_size': this.itemsPerPage
        }
        
        if (this.search) {
          queryParams.serial_number = this.search
        }

        if (this.productNames) {
          queryParams.product_names = this.productNames
        }
        
        if (paginationToken) {
          queryParams.pagination_token = paginationToken
        }
        
        this.axios.get('/devices', {
          'params': queryParams,
          paramsSerializer: {
            indexes: null
          }
        }).then((response) => {
          console.log('AdminDeviceListing searchDevices response', response)
          
          if (response.data.devices.length > 0) {  
            this.items = response.data.devices.map((device) => this.$devices.getDevice(device.device_guid, device))
            
            if (response.data.pagination_token) {
              this.$set(this.paginationTokens, paginationIndex, response.data.pagination_token)
            }
          }
          this.loading = false
        }).catch((error) => {
          console.log('AdminDeviceListing searchDevices error', error)
          this.error = true
          this.errorText = this.$helpers.parseError(error)
          this.loading = false
        })
      },
      
      manageDevice(item) {
        console.log('AdminDeviceListing manageDevice', item)
        this.$router.push('/devices/' + item.device_guid)
      }, 
      
    }
  }
</script>